// Format date
export const formatDate = (timestamp, format = 'relative') => {
  if (!timestamp) return 'Unknown time';

  const date = new Date(timestamp * 1000);

  // Check if date is valid
  if (isNaN(date.getTime())) return 'Invalid date';

  if (format === 'dd-mm-yyyy') {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // Default relative format
  const now = new Date();
  const diff = now - date;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return date.toLocaleDateString();
};

// Check if user is online
export const isOnline = (lastActive) => {
  const now = Date.now() / 1000;
  const diff = now - lastActive;
  return diff < 900; // 15 minutes
};

// Get first name
export const getFirstName = (fullName) => {
  return fullName ? fullName.split(' ')[0] : '';
};

// Convert datetime to timestamp
export const datetimeToTimestamp = (datetime) => {
  if (!datetime) return 0;
  if (typeof datetime === 'number') return datetime;
  if (typeof datetime === 'string') {
    return Math.floor(new Date(datetime).getTime() / 1000);
  }
  if (datetime instanceof Date) {
    return Math.floor(datetime.getTime() / 1000);
  }
  return 0;
};

// Calculate distance
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return Math.round(d);
};

const deg2rad = (deg) => {
  return deg * (Math.PI / 180);
};

// Format number
export const formatNumber = (num) => {
  return new Intl.NumberFormat().format(num);
};

// Get API base URL for uploads
const getApiBaseUrl = () => {
  // In production, use full API URL
  // In development, Vite proxy will handle /uploads/
  if (typeof __VITE_API_URL__ !== 'undefined' && __VITE_API_URL__) {
    // __VITE_API_URL__ is 'https://api.cleanurphone.xyz/api'
    // We need to remove the '/api' suffix to get base URL
    const apiUrl = __VITE_API_URL__;
    // Use URL parsing for safe manipulation
    try {
      const url = new URL(apiUrl);
      // Return just the origin (protocol + host)
      return url.origin; // 'https://api.cleanurphone.xyz'
    } catch (e) {
      console.error('Invalid API URL:', apiUrl, e);
      return 'https://api.cleanurphone.xyz';
    }
  }
  return 'https://api.cleanurphone.xyz';
};

// Get full URL for uploaded photo
export const getUploadUrl = (path) => {
  if (!path) return null;
  
  // If already a full URL, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // If it's a data URL (for placeholders), return as is
  if (path.startsWith('data:')) {
    return path;
  }
  
  // Use API base URL for uploads
  const apiBase = getApiBaseUrl();
  return `${apiBase}/uploads/${path}`;
};

// Get profile picture URL
export const getProfilePicture = (user, domain = '') => {
  if (!user) return `/assets/images/blank-photo.png`;

  if (!user.profile_picture || user.profile_picture === 'default_avatar.png') {
    return `/assets/images/blank-photo.png`;
  }

  // Use API base URL for uploads
  const apiBase = getApiBaseUrl();
  const imageUrl = `${apiBase}/uploads/${user.profile_picture}`;
  return imageUrl;
};

// Get cover photo URL
export const getCoverPhoto = (user, domain = '') => {
  if (!user) return `/assets/images/covers/0.png`;
  if (!user.cover_photo) {
    return `/assets/images/covers/0.png`;
  }

  // For mock users (user-2, user-3, user-4), return placeholder since images may not exist
  if (user.cover_photo && user.cover_photo.startsWith('user-') &&
      (user.cover_photo.includes('user-2') ||
       user.cover_photo.includes('user-3') ||
       user.cover_photo.includes('user-4'))) {
    // Create a data URL for a simple colored cover placeholder
    const colors = { 'user-2': '#FF6B6B', 'user-3': '#4ECDC4', 'user-4': '#45B7D1' };
    const userId = user.cover_photo.split('-')[1];
    const color = colors[`user-${userId}`] || '#666';

    // Return a simple SVG data URL as cover placeholder (wider than tall)
    const svgData = `<svg width="400" height="150" xmlns="http://www.w3.org/2000/svg"><rect width="400" height="150" fill="${color}"/><text x="200" y="75" text-anchor="middle" fill="white" font-family="Arial" font-size="18" font-weight="bold">${user.full_name || `User ${userId}`} - Cover</text></svg>`;
    return `data:image/svg+xml;base64,${btoa(svgData)}`;
  }

  // Use API base URL for uploads
  const apiBase = getApiBaseUrl();
  return `${apiBase}/uploads/${user.cover_photo}`;
};

// Validate email
export const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

// Validate password
export const isValidPassword = (password) => {
  return password && password.length >= 6;
};

// Get age range label
export const getAgeRangeLabel = (min, max) => {
  return `${min} - ${max} years`;
};

// Get distance range label
export const getDistanceRangeLabel = (min, max) => {
  return `${min} - ${max} km`;
};

// Truncate text
export const truncateText = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Get sexual orientation label
export const getSexualOrientationLabel = (value) => {
  const labels = {
    1: 'Straight',
    2: 'Gay',
    3: 'Lesbian',
    4: 'Bisexual',
  };
  return labels[value] || 'Unknown';
};

// Get gender label
export const getGenderLabel = (value) => {
  const labels = {
    Male: 'Male',
    Female: 'Female',
  };
  return labels[value] || 'Unknown';
};

// Add comma if needed
export const addCommaIfNeeded = (text) => {
  return text ? ', ' : '';
};

// Get user badges
export const getUserBadges = (user) => {
  const badges = [];
  if (user.is_vip) badges.push('vip');
  if (user.is_popular) badges.push('popular');
  if (user.is_new) badges.push('new');
  return badges;
};

// Format credits
export const formatCredits = (credits) => {
  return formatNumber(credits || 0);
};

// Get geolocation
export const getGeolocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        reject(error);
      }
    );
  });
};

